import './index.scss';

import * as RoutesModule from 'Routes';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider, createBrowserRouter, createRoutesFromElements } from 'react-router';
import { StrictMode, createContext, useContext } from 'react';

import { AxiosServiceFactory } from 'services/AxiosServiceFactory';
import CodegenSetupService from 'services/CodegenSetupService';
import { ConfigurationService } from 'services/ConfigurationService';
import { createRoot } from 'react-dom/client';
import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';
import translationsEn from './locales/en/translation-en.json';

const rootElement = document.getElementById('root');
const root = createRoot(rootElement!);

type Stores = {
  configurationService: ConfigurationService;
  codegenSetupService: CodegenSetupService;
};

const StoresContext = createContext<Stores | null>(null);

export const useStores = () => {
  const stores = useContext(StoresContext);
  if (!stores) {
    throw new Error('useStores must be used within a StoreProvider.');
  }
  return stores;
};

const langResources = {
  en: { translation: translationsEn },
};

export const isLangAvailable = (lang: string) => {
  return Object.keys(langResources).includes(lang) ? lang : '';
};

async function RenderApp() {
  const configurationService = new ConfigurationService();
  const serviceFactory = new AxiosServiceFactory(configurationService);
  const apiServiceAxiosInstance = await serviceFactory.createInstance();

  const codegenSetupService = new CodegenSetupService(apiServiceAxiosInstance);

  const stores: Stores = {
    configurationService,
    codegenSetupService,
  };

  await i18next.use(initReactI18next).init({
    resources: langResources,
    lng: isLangAvailable(navigator.language.slice(0, 2)) || 'en',
    fallbackLng: 'en',
    interpolation: { escapeValue: false },
  });

  const router = createBrowserRouter(createRoutesFromElements(RoutesModule.routes));
  const queryClient = new QueryClient();

  root.render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <StoresContext.Provider value={stores}>
          <RouterProvider router={router} />
        </StoresContext.Provider>
      </QueryClientProvider>
    </StrictMode>
  );
}

RenderApp();
